import { z } from "zod";

const text = (max: number) => z.string().trim().min(1).max(max);
export const rescueInvestigationFocusSchema = z.enum([
  "commitments",
  "customer_updates",
  "both",
]);
export const rescueInvestigationResultSchema = z
  .object({
    focus: rescueInvestigationFocusSchema,
    summary: text(1000),
    findings: z
      .array(
        z
          .object({
            kind: z.enum(["commitment", "source_update", "evidence_gap"]),
            title: text(300),
            detail: text(2000),
            evidenceIds: z.array(text(200)).max(20),
          })
          .strict(),
      )
      .max(40),
    citations: z
      .array(
        z
          .object({
            evidenceId: text(200),
            sourceLabel: text(200),
            occurredAt: text(64),
            quote: text(500),
          })
          .strict(),
      )
      .max(50),
    assessedAt: z.string().datetime(),
  })
  .strict();

export const rescueConversationSchema = z
  .object({
    id: z.string().uuid(),
    rescueCaseId: z.string().uuid(),
    question: text(500),
    status: z.enum(["pending", "completed", "cancelled", "expired", "stale"]),
    focus: rescueInvestigationFocusSchema.nullable(),
    result: rescueInvestigationResultSchema.nullable(),
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    resolvedAt: z.string().datetime().nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const completed = value.status === "completed";
    if (
      completed
        ? !value.result || !value.focus || value.result.focus !== value.focus
        : value.result !== null || value.focus !== null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["result"],
        message:
          "Only completed investigations have a matching result and focus.",
      });
    }
    if ((value.status === "pending") !== (value.resolvedAt === null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["resolvedAt"],
        message: "Terminal investigations require a resolution timestamp.",
      });
    }
  });
export const createRescueConversationRequestSchema = z.object({}).strict();
export const respondRescueConversationRequestSchema = z
  .object({
    answer: z.enum(["commitments", "customer_updates", "both", "cancel"]),
  })
  .strict();
export const rescueConversationResponseSchema = z
  .object({ conversation: rescueConversationSchema })
  .strict();
export const getRescueConversationResponseSchema = z
  .object({ conversation: rescueConversationSchema.nullable() })
  .strict();
export const rescueConversationErrorResponseSchema = z
  .object({
    error: text(500),
    details: z
      .object({
        code: z.enum([
          "investigation_expired",
          "investigation_stale",
          "investigation_conflict",
        ]),
      })
      .strict(),
  })
  .strict();
export type RescueInvestigationFocus = z.infer<
  typeof rescueInvestigationFocusSchema
>;
export type RescueInvestigationResult = z.infer<
  typeof rescueInvestigationResultSchema
>;
export type RescueConversation = z.infer<typeof rescueConversationSchema>;
export type RespondRescueConversationRequest = z.infer<
  typeof respondRescueConversationRequestSchema
>;
export type RescueConversationResponse = z.infer<
  typeof rescueConversationResponseSchema
>;
export type GetRescueConversationResponse = z.infer<
  typeof getRescueConversationResponseSchema
>;
